import { Building2, Check, ChevronDown } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { cn } from "@/lib/cn";
import {
  membershipRepository,
  type Membership,
} from "@/repositories/membershipRepository";
import { useAuthStore } from "@/stores/authStore";

/**
 * Which business you are working in.
 *
 * **Invisible to almost everybody**, and that is the design. A shopkeeper
 * belongs to one merchant and always will; rendering a control that offers them
 * a choice of one would be teaching a concept to the ninety-nine per cent of
 * users it will never apply to. It appears only for the accountant with four
 * clients and the franchise manager with six.
 *
 * Distinct from the Branch ▸ Section selector beside it, and deliberately not
 * merged with it. That control *narrows* within one business — an owner picking
 * one of their counters. This one *changes* which business is answering, which
 * means different data, different colleagues and a different role. Putting them
 * in one menu would make "Vosloorus" and "Nkosi Trading" look like the same kind
 * of choice.
 *
 * The role is shown beside each name because it is the thing people get wrong:
 * somebody who is an owner at their own shop and a read-only bookkeeper at three
 * others needs to know which hat they are about to put on, before they act
 * rather than after they are refused.
 */
export function MerchantSwitcher() {
  const session = useAuthStore((s) => s.session);
  const setActiveMerchant = useAuthStore((s) => s.setActiveMerchant);

  const [memberships, setMemberships] = useState<Membership[]>([]);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!session) return;
    // Anything other than a list of memberships hides the control, and the
    // shape is checked rather than trusted. This is not defensiveness for its
    // own sake: the switcher renders inside the application shell, so an
    // unexpected payload here took down every screen behind it — the top bar is
    // the wrong place to discover that a response was not what you assumed.
    //
    // Failing to an empty list is also the right *product* answer: a switcher
    // that cannot load offers a choice it cannot honour, and the overwhelming
    // majority of users have one business and never see it either way.
    void membershipRepository
      .mine()
      .then((rows) => setMemberships(Array.isArray(rows) ? rows : []))
      .catch(() => setMemberships([]));
  }, [session?.user.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return;
    const onClickAway = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickAway);
    return () => document.removeEventListener("mousedown", onClickAway);
  }, [open]);

  if (!session || memberships.length < 2) return null;

  const home = memberships.find((membership) => membership.is_home);
  const activeId = session.activeMerchantId ?? home?.id ?? null;
  const active =
    memberships.find((membership) => membership.id === activeId) ?? home;

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="flex h-touch items-center gap-2.5 rounded-control border border-line bg-surface px-3 text-sm text-ink-900 hover:bg-canvas"
      >
        <Building2 className="size-4 shrink-0 text-brand-500" aria-hidden />
        <span className="max-w-52 truncate">
          {active?.name ?? "Choose a business"}
        </span>
        <ChevronDown className="size-4 shrink-0 text-ink-400" aria-hidden />
      </button>

      {open && (
        <ul
          role="listbox"
          aria-label="Choose a business"
          className="absolute right-0 z-20 mt-1 max-h-80 w-[min(20rem,calc(100vw-1.5rem))] overflow-y-auto rounded-card border border-line bg-surface py-1 shadow-pop"
        >
          {memberships.map((membership) => (
            <li key={membership.id}>
              <button
                type="button"
                role="option"
                aria-selected={membership.id === active?.id}
                onClick={() => {
                  // Home is stored as null rather than as its own id: it is the
                  // server's default, so sending nothing and sending your own
                  // merchant mean the same thing — and null survives a merchant
                  // being renamed or re-keyed.
                  void setActiveMerchant(
                    membership.is_home ? null : membership.id,
                  );
                  setOpen(false);
                }}
                className="flex w-full items-start gap-2.5 px-3 py-2.5 text-left hover:bg-canvas"
              >
                <Check
                  className={cn(
                    "mt-0.5 size-4 shrink-0",
                    membership.id === active?.id
                      ? "text-brand-500"
                      : "text-transparent",
                  )}
                  aria-hidden
                />
                <span className="min-w-0">
                  <span className="block truncate text-sm text-ink-900">
                    {membership.name}
                  </span>
                  <span className="block truncate text-xs text-ink-500">
                    {membership.roles.length > 0
                      ? membership.roles.join(", ").toLowerCase()
                      : "no role yet"}
                    {membership.is_home && " · your own"}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
